import type { UserSettings } from '../types';

const normalizePathname = (pathname: string): string => {
    const path = (pathname || '/').split('?')[0].split('#')[0] || '/';
    if (path === '/') return path;
    return path.replace(/\/+$/, '');
};

const matchesPath = (pathname: string, pattern: string): boolean => {
    const path = normalizePathname(pathname);
    const normalizedPattern = normalizePathname(pattern);
    return path === normalizedPattern || path.startsWith(`${normalizedPattern}/`);
};

const PRIVATE_OR_LOW_VALUE_PREFIXES = [
    '/dang-nhap',
    '/chon-vai-tro',
    '/sap-ra-mat',
    '/cua-hang-credit',
    '/lich-su-giao-dich',
    '/cai-dat',
    '/affiliate-dashboard',
    '/cong-cu-giao-vien',
    '/bang-dieu-khien-phu-huynh',
    '/lo-trinh-hoc-tap',
    '/bang-hoc-cua-con',
    '/quan-ly-lop-hoc',
    '/tham-gia-lop',
    '/tham-gia-lop-hoc',
    '/bai-hoc',
    '/luyen-phat-am',
    '/hoc-ve',
    '/ke-chuyen-lich-su',
    '/tro-ly-van-hoc',
    '/giai-thich-khoa-hoc',
    '/phong-lab-ai',
    '/thu-thach-dao-duc-ai',
    '/kiem-chung-thong-tin-ai',
    '/hanh-trinh-kham-pha',
    '/slide-mau',
    '/soan-thao',
    '/thiet-ke',
    '/thiet-ke-3d',
    '/mo-phong-khoa-hoc',
    '/video-tuong-tac',
    '/kiem-tra-kien-thuc',
    '/giao-an',
    '/de-kiem-tra',
    '/tro-choi',
    '/trinh-chieu',
    '/kho-bai-tap',
    '/kho-hoat-dong',
    '/bai-hoc-ve',
    '/thong-bao',
    '/bai-tap',
    '/nhiem-vu',
];

const PRIVATE_OR_LOW_VALUE_EXACT_PATHS = new Set([
    '/404',
]);

const CREATOR_PREFIXES = [
    '/tao-giao-an',
    '/tao-de-kiem-tra',
    '/tao-tro-choi',
    '/tao-trinh-chieu',
    '/tao-bai-tap',
    '/tao-video',
    '/tao-sang-kien-kinh-nghiem',
    '/hoat-dong-sang-tao',
    '/kiem-tra-mieng',
    '/tao-skkn',
];

const PUBLIC_CONTENT_PREFIXES = [
    '/gioi-thieu',
    '/huong-dan',
    '/dieu-khoan',
    '/chinh-sach-bao-mat',
    '/thu-vien-cong-dong',
    '/chia-se',
    '/tinh-nang',
    '/thu-tao', // Guest try pages — ad-eligible, no login required
];

const DISALLOWED_SLOT_IDS = new Set([
    'loading_banner',
    'sidebar_banner',
]);

export const isPublicContentPath = (pathname: string): boolean => {
    const path = normalizePathname(pathname);
    return path === '/' || PUBLIC_CONTENT_PREFIXES.some((prefix) => matchesPath(path, prefix));
};

export const shouldNoindexPath = (pathname: string): boolean => {
    const path = normalizePathname(pathname);
    if (PRIVATE_OR_LOW_VALUE_EXACT_PATHS.has(path)) return true;
    if (path.endsWith('/edit') || path.includes('/edit/')) return true;
    if (CREATOR_PREFIXES.some((prefix) => matchesPath(path, prefix))) return true;
    if (PRIVATE_OR_LOW_VALUE_PREFIXES.some((prefix) => matchesPath(path, prefix))) return true;
    return !isPublicContentPath(path);
};

export const getRobotsContentForPath = (pathname: string): string => (
    shouldNoindexPath(pathname)
        ? 'noindex,nofollow,noarchive'
        : 'index,follow,max-image-preview:large'
);

export const canShowDisplayAds = (pathname: string, _user?: UserSettings | null): boolean => {
    if (shouldNoindexPath(pathname)) return false;
    return isPublicContentPath(pathname);
};

export const canShowAdSlot = (
    pathname: string,
    slotId?: string,
    user?: UserSettings | null,
): boolean => {
    if (slotId && DISALLOWED_SLOT_IDS.has(slotId)) return false;
    return canShowDisplayAds(pathname, user);
};
